import React from 'react';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { StaticImageData } from 'next/image';
import HeroFormCTA from '~app/components/HeroFormCTA';
import useStyles from './useStyles';
import { RebrandV2IntlProps, HeroFormCTAProps } from '~helpers/types';
import BrandLogoWithText from '~app/components/staticThanksHeader/BrandLogoWithText';
import rebrandFocusImgV1 from '~static/images/dareToAsk/rebrandEp-default.webp';
import rebrandV2BrDesktopImg from '~static/images/brazilExp/br-desktop.webp';
import rebrandIntlV2Desktop from '~static/images/rebrandIntl/rebrandIntl-desktop.webp';
import rebrandEpV2DesktopImg from '~static/images/rebrandEpV2/rebrandEngV2-desk.webp';
import ImageComponent from '~app/components/endpages/components/Image';

const imageMap: { [key: string]: string | StaticImageData } = {
  RebrandEpFormV1: rebrandFocusImgV1,
  epFormBrazil: rebrandV2BrDesktopImg,
  rebrandEpFormIntl: rebrandIntlV2Desktop,
  rebrandEpFormV2: rebrandEpV2DesktopImg,
  default: rebrandFocusImgV1,
};

const getImageVariant = (utSource2: string): string | StaticImageData => {
  return imageMap[utSource2] || imageMap.default;
};

type ColorVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'upgrade'
  | 'info'
  | 'muted'
  | 'dark'
  | 'light'
  | 'darkMuted'
  | 'lightMuted'
  | 'link';

export type Props = {
  color?: ColorVariant;
  boldHeader: string;
  subHeader: string;
  utSource: string;
  utSource2: string;
  formCopy: Omit<HeroFormCTAProps, 'className'>;
  calloutItem3?: RebrandV2IntlProps;
};

const HeroFormCallout = ({
  utSource,
  utSource2,
  color,
  boldHeader,
  subHeader: subHeaderContent,
  formCopy,
  calloutItem3,
}: Props): React.ReactElement => {
  const classes = useStyles({ utSource2 });

  const isBrazilForm = utSource2 === 'epFormBrazil';
  const isV2Form = ['rebrandEpFormIntl', 'rebrandEpFormV2'].includes(utSource2 ?? '');
  const headerText = isBrazilForm || isV2Form ? calloutItem3?.brazilFormHeader : boldHeader;
  const subHeaderText = isV2Form ? calloutItem3?.formSubHeader : subHeaderContent;
  const isBrazilSubHeader = isBrazilForm && calloutItem3?.brazilFormSubHeader;

  if (utSource2 === 'RebrandEpFormV1') {
    return (
      <Box className={classes.display}>
        <Box
          display="flex"
          flexJustify="center"
          flexDirection="column"
          data-testid="HeroFormCallout"
          className={classes.wrapperV1}
        >
          <Box className={classes.contentContainer}>
            <HeroFormCTA
              className={classes.ctaComponentV1}
              utSource={utSource}
              utSource2={utSource2}
              boldHeader={boldHeader}
              subHeader={subHeaderContent}
              {...formCopy}
            />
            <ImageComponent
              src={getImageVariant(utSource2)}
              alt="SurveyMonkey Branding"
              className={`${classes.imageBase} ${classes.imageV1}`}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexJustify="center"
      flexDirection="column"
      data-testid="HeroFormCallout"
      className={classes.wrapper}
    >
      {isV2Form && <BrandLogoWithText utSource={utSource} utSource2={utSource2} />}
      <Box className={classes.header}>
        <Typography component="section" variant="sectionTitle" color={color} weight="medium">
          {headerText}
        </Typography>
        <Box pt={3} className={isV2Form ? classes.intlSubheader : classes.subHeader}>
          <Typography component="div" variant="body" color={color}>
            {isBrazilForm ? isBrazilSubHeader : subHeaderText}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.contentContainer}>
        <HeroFormCTA className={classes.ctaComponent} utSource={utSource} utSource2={utSource2} {...formCopy} />
        <ImageComponent
          src={getImageVariant(utSource2)}
          alt="SurveyMonkey Branding"
          className={`${classes.imageBase} ${isV2Form ? classes.imageV2 : classes.imageResponsive}`}
        />
      </Box>
    </Box>
  );
};

export default HeroFormCallout;
